import { serializeFilters, type QueryFilters } from '../core/queryKeyUtils';

export const receptionKeys = {
  all: ['reception'] as const,
  bundle: {
    home: (filters?: QueryFilters) =>
      [...receptionKeys.all, 'bundle', 'home', serializeFilters(filters)] as const,
    homeScope: () =>
      [...receptionKeys.all, 'bundle', 'home'] as const,
    tasks: (filters?: QueryFilters) =>
      [...receptionKeys.all, 'bundle', 'tasks', serializeFilters(filters)] as const,
    tasksScope: () =>
      [...receptionKeys.all, 'bundle', 'tasks'] as const,
    finance: (filters?: QueryFilters) =>
      [...receptionKeys.all, 'bundle', 'finance', serializeFilters(filters)] as const,
    financeScope: () =>
      [...receptionKeys.all, 'bundle', 'finance'] as const,
    students: (filters?: QueryFilters) =>
      [...receptionKeys.all, 'bundle', 'students', serializeFilters(filters)] as const,
    studentsScope: () =>
      [...receptionKeys.all, 'bundle', 'students'] as const,
    groups: (filters?: QueryFilters) =>
      [...receptionKeys.all, 'bundle', 'groups', serializeFilters(filters)] as const,
    groupsScope: () =>
      [...receptionKeys.all, 'bundle', 'groups'] as const,
    studentInvoice: (studentId: number | string, filters?: QueryFilters) =>
      [...receptionKeys.all, 'bundle', 'student-invoice', studentId, serializeFilters(filters)] as const,
    studentInvoiceScope: (studentId: number | string) =>
      [...receptionKeys.all, 'bundle', 'student-invoice', studentId] as const,
    studentInvoiceAll: () =>
      [...receptionKeys.all, 'bundle', 'student-invoice'] as const,
    attendanceSession: (sessionId: number | string, filters?: QueryFilters) =>
      [...receptionKeys.all, 'bundle', 'attendance-session', sessionId, serializeFilters(filters)] as const,
    attendanceSessionScope: (sessionId: number | string) =>
      [...receptionKeys.all, 'bundle', 'attendance-session', sessionId] as const,
    attendanceSessionAll: () =>
      [...receptionKeys.all, 'bundle', 'attendance-session'] as const,
    salesInvoices: (filters?: QueryFilters) =>
      [...receptionKeys.all, 'bundle', 'sales-invoices', serializeFilters(filters)] as const,
    salesInvoicesScope: () =>
      [...receptionKeys.all, 'bundle', 'sales-invoices'] as const,
    placementPayments: (filters?: QueryFilters) =>
      [...receptionKeys.all, 'bundle', 'placement-payments', serializeFilters(filters)] as const,
    placementPaymentsScope: () =>
      [...receptionKeys.all, 'bundle', 'placement-payments'] as const,
    courseInterests: (filters?: QueryFilters) =>
      [...receptionKeys.all, 'bundle', 'course-interests', serializeFilters(filters)] as const,
    courseInterestsScope: () =>
      [...receptionKeys.all, 'bundle', 'course-interests'] as const,
    requests: (filters?: QueryFilters) =>
      [...receptionKeys.all, 'bundle', 'requests', serializeFilters(filters)] as const,
    requestsScope: () =>
      [...receptionKeys.all, 'bundle', 'requests'] as const,
    superLogs: (filters?: QueryFilters) =>
      [...receptionKeys.all, 'bundle', 'super-logs', serializeFilters(filters)] as const,
    superLogsScope: () =>
      [...receptionKeys.all, 'bundle', 'super-logs'] as const,
    languageAdminMaterials: (filters?: QueryFilters) =>
      [...receptionKeys.all, 'bundle', 'language-admin-materials', serializeFilters(filters)] as const,
    languageAdminMaterialsScope: () =>
      [...receptionKeys.all, 'bundle', 'language-admin-materials'] as const,
  },
  branches: () => [...receptionKeys.all, 'branches'] as const,
  courses: (filters?: QueryFilters) =>
    [...receptionKeys.all, 'courses', serializeFilters(filters)] as const,
  dashboardStats: (filters?: QueryFilters) =>
    [...receptionKeys.all, 'dashboard', 'stats', serializeFilters(filters)] as const,
  students: {
    all: () => [...receptionKeys.all, 'students'] as const,
    list: (filters?: QueryFilters) =>
      [...receptionKeys.students.all(), 'list', serializeFilters(filters)] as const,
    detail: (studentId: number | string) =>
      [...receptionKeys.students.all(), 'detail', studentId] as const,
    comments: (studentId: number | string) =>
      [...receptionKeys.students.detail(studentId), 'comments'] as const,
    history: (studentId: number | string) =>
      [...receptionKeys.students.detail(studentId), 'history'] as const,
    branchTransfers: (studentId: number | string) =>
      [...receptionKeys.students.detail(studentId), 'branch-transfers'] as const,
    dashboardPending: (filters?: QueryFilters) =>
      [...receptionKeys.students.all(), 'dashboard', 'pending', serializeFilters(filters)] as const,
    dashboardReady: (filters?: QueryFilters) =>
      [...receptionKeys.students.all(), 'dashboard', 'ready', serializeFilters(filters)] as const,
  },
  waiting: {
    all: () => [...receptionKeys.all, 'waiting'] as const,
    insights: (filters?: QueryFilters) =>
      [...receptionKeys.waiting.all(), 'insights', serializeFilters(filters)] as const,
  },
  groups: {
    all: () => [...receptionKeys.all, 'groups'] as const,
    list: (filters?: QueryFilters) =>
      [...receptionKeys.groups.all(), 'list', serializeFilters(filters)] as const,
    members: (groupId: number | string) =>
      [...receptionKeys.groups.all(), 'members', groupId] as const,
  },
  live: {
    all: () => [...receptionKeys.all, 'live'] as const,
    activeGroups: () => [...receptionKeys.live.all(), 'active-groups'] as const,
    status: (groupId: number | string) =>
      [...receptionKeys.live.all(), 'status', groupId] as const,
  },
  courseInterests: {
    all: () => [...receptionKeys.all, 'course-interests'] as const,
    list: (filters?: QueryFilters) =>
      [...receptionKeys.courseInterests.all(), 'list', serializeFilters(filters)] as const,
  },
  requests: {
    all: () => [...receptionKeys.all, 'requests'] as const,
    list: (filters?: QueryFilters) =>
      [...receptionKeys.requests.all(), 'list', serializeFilters(filters)] as const,
    filters: () => [...receptionKeys.requests.all(), 'filters'] as const,
  },
  announcements: {
    all: () => [...receptionKeys.all, 'announcements'] as const,
    list: () => [...receptionKeys.announcements.all(), 'list'] as const,
    pending: () => [...receptionKeys.announcements.all(), 'pending'] as const,
  },
  materials: {
    all: () => [...receptionKeys.all, 'materials'] as const,
    courses: () => [...receptionKeys.materials.all(), 'courses'] as const,
    sections: (filters?: QueryFilters) =>
      [...receptionKeys.materials.all(), 'sections', serializeFilters(filters)] as const,
    groupOptions: (filters?: QueryFilters) =>
      [...receptionKeys.materials.all(), 'group-options', serializeFilters(filters)] as const,
    groupOptionsScope: () => [...receptionKeys.materials.all(), 'group-options'] as const,
    list: (filters?: QueryFilters) =>
      [...receptionKeys.materials.all(), 'list', serializeFilters(filters)] as const,
  },
  attendance: {
    all: () => [...receptionKeys.all, 'attendance'] as const,
    groups: (filters?: QueryFilters) =>
      [...receptionKeys.attendance.all(), 'groups', serializeFilters(filters)] as const,
    groupDetail: (groupId: number | string) =>
      [...receptionKeys.attendance.all(), 'group', groupId] as const,
    matrix: (groupId: number | string) =>
      [...receptionKeys.attendance.all(), 'matrix', groupId] as const,
    session: (sessionId: number | string) =>
      [...receptionKeys.attendance.all(), 'session', sessionId] as const,
  },
  chat: {
    all: () => [...receptionKeys.all, 'chat'] as const,
    contacts: (filters?: QueryFilters) =>
      [...receptionKeys.chat.all(), 'contacts', serializeFilters(filters)] as const,
    search: (filters?: QueryFilters) =>
      [...receptionKeys.chat.all(), 'search', serializeFilters(filters)] as const,
  },
  exams: {
    all: () => [...receptionKeys.all, 'exams'] as const,
    list: (filters?: QueryFilters) =>
      [...receptionKeys.exams.all(), 'list', serializeFilters(filters)] as const,
    detail: (examId: number | string) =>
      [...receptionKeys.exams.all(), 'detail', examId] as const,
    importStatus: (jobId: number | string) =>
      [...receptionKeys.exams.all(), 'import-status', jobId] as const,
    importJobs: (filters?: QueryFilters) =>
      [...receptionKeys.exams.all(), 'import-jobs', serializeFilters(filters)] as const,
  },
  attempts: {
    all: () => [...receptionKeys.all, 'attempts'] as const,
    detail: (attemptId: number | string) =>
      [...receptionKeys.attempts.all(), 'detail', attemptId] as const,
  },
  dailySummary: () => [...receptionKeys.all, 'daily-summary'] as const,
  dailyReports: {
    all: () => [...receptionKeys.all, 'daily-reports'] as const,
    list: (filters?: QueryFilters) =>
      [...receptionKeys.dailyReports.all(), 'list', serializeFilters(filters)] as const,
    unreadCount: () => [...receptionKeys.dailyReports.all(), 'unread-count'] as const,
  },
  studentAdmin: {
    all: () => [...receptionKeys.all, 'student-admin'] as const,
    inactive: () => [...receptionKeys.studentAdmin.all(), 'inactive'] as const,
    search: (filters?: QueryFilters) =>
      [...receptionKeys.studentAdmin.all(), 'search', serializeFilters(filters)] as const,
  },
  certificates: {
    all: () => [...receptionKeys.all, 'certificates'] as const,
    list: (filters?: QueryFilters) =>
      [...receptionKeys.certificates.all(), 'list', serializeFilters(filters)] as const,
    listScope: () => [...receptionKeys.certificates.all(), 'list'] as const,
    students: (filters?: QueryFilters) =>
      [...receptionKeys.certificates.all(), 'students', serializeFilters(filters)] as const,
  },
  salesConsult: {
    all: () => [...receptionKeys.all, 'sales-consult'] as const,
    queue: (filters?: QueryFilters) =>
      [...receptionKeys.salesConsult.all(), 'queue', serializeFilters(filters)] as const,
    slots: (filters?: QueryFilters) =>
      [...receptionKeys.salesConsult.all(), 'slots', serializeFilters(filters)] as const,
  },
  calendar: {
    all: () => [...receptionKeys.all, 'calendar'] as const,
    sessions: (filters?: QueryFilters) =>
      [...receptionKeys.calendar.all(), 'sessions', serializeFilters(filters)] as const,
  },
  finance: {
    all: () => [...receptionKeys.all, 'finance'] as const,
    meta: () => [...receptionKeys.finance.all(), 'meta'] as const,
    salesInvoiceFilters: () => [...receptionKeys.finance.all(), 'sales-invoice-filters'] as const,
    salesInvoices: (filters?: QueryFilters) =>
      [...receptionKeys.finance.all(), 'sales-invoices', serializeFilters(filters)] as const,
    refundFilters: () => [...receptionKeys.finance.all(), 'refund-filters'] as const,
    refundsScope: () => [...receptionKeys.finance.all(), 'refunds'] as const,
    refunds: (filters?: QueryFilters) =>
      [...receptionKeys.finance.all(), 'refunds', serializeFilters(filters)] as const,
    summary: (filters?: QueryFilters) =>
      [...receptionKeys.finance.all(), 'summary', serializeFilters(filters)] as const,
    report: (filters?: QueryFilters) =>
      [...receptionKeys.finance.all(), 'report', serializeFilters(filters)] as const,
    incomes: (filters?: QueryFilters) =>
      [...receptionKeys.finance.all(), 'incomes', serializeFilters(filters)] as const,
    expenses: (filters?: QueryFilters) =>
      [...receptionKeys.finance.all(), 'expenses', serializeFilters(filters)] as const,
    proSummary: (filters?: QueryFilters) =>
      [...receptionKeys.finance.all(), 'pro-summary', serializeFilters(filters)] as const,
    proInvoices: (filters?: QueryFilters) =>
      [...receptionKeys.finance.all(), 'pro-invoices', serializeFilters(filters)] as const,
    proStudent: (studentId: number | string, filters?: QueryFilters) =>
      [...receptionKeys.finance.all(), 'pro-student', studentId, serializeFilters(filters)] as const,
  },
  super: {
    pendingBadges: () => [...receptionKeys.all, 'super', 'pending-badges'] as const,
  },
  placement: {
    all: () => [...receptionKeys.all, 'placement'] as const,
    payments: (filters?: QueryFilters) =>
      [...receptionKeys.placement.all(), 'payments', serializeFilters(filters)] as const,
    paymentStats: () => [...receptionKeys.placement.all(), 'payment-stats'] as const,
  },
  summerTraining: {
    all: () => [...receptionKeys.all, 'summer-training'] as const,
    workspace: () => [...receptionKeys.summerTraining.all(), 'workspace'] as const,
    receipt: (receiptId: number | string) =>
      [...receptionKeys.summerTraining.all(), 'receipt', receiptId] as const,
  },
  quarter: {
    all: () => [...receptionKeys.all, 'quarter'] as const,
    settings: () => [...receptionKeys.quarter.all(), 'alert-settings'] as const,
  },
  invoices: {
    all: () => [...receptionKeys.all, 'invoices'] as const,
    receipt: (invoiceId: number | string) =>
      [...receptionKeys.invoices.all(), 'receipt', invoiceId] as const,
    studentScope: (studentId: number | string) =>
      [...receptionKeys.invoices.all(), 'student', studentId] as const,
    studentList: (studentId: number | string, filters?: QueryFilters) =>
      [...receptionKeys.invoices.studentScope(studentId), 'list', serializeFilters(filters)] as const,
    installmentsScope: (invoiceId: number | string) =>
      [...receptionKeys.invoices.all(), 'installments', invoiceId] as const,
    installments: (invoiceId: number | string, filters?: QueryFilters) =>
      [...receptionKeys.invoices.installmentsScope(invoiceId), serializeFilters(filters)] as const,
  },
  languageAdmin: {
    all: () => [...receptionKeys.all, 'language-admin'] as const,
    placementExams: (filters?: QueryFilters) =>
      [...receptionKeys.languageAdmin.all(), 'placement-exams', serializeFilters(filters)] as const,
    attempts: (filters?: QueryFilters) =>
      [...receptionKeys.languageAdmin.all(), 'attempts', serializeFilters(filters)] as const,
    materials: (filters?: QueryFilters) =>
      [...receptionKeys.languageAdmin.all(), 'materials', serializeFilters(filters)] as const,
  },
} as const;
